/**
 * Client copy of server pageRotation helpers (visual ↔ media coordinates).
 * Keep in sync with server/src/utils/pageRotation.ts
 */

export interface PageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function normalizeRotation(degrees: number): 0 | 90 | 180 | 270 {
  const r = ((Math.round(degrees) % 360) + 360) % 360;
  if (r === 90 || r === 180 || r === 270) return r;
  return 0;
}

export function visualPageDimensions(
  mediaWidth: number,
  mediaHeight: number,
  rotationDegrees: number
): { width: number; height: number } {
  const r = normalizeRotation(rotationDegrees);
  if (r === 90 || r === 270) {
    return { width: mediaHeight, height: mediaWidth };
  }
  return { width: mediaWidth, height: mediaHeight };
}

export function visualRectToMediaBox(
  rect: PageRect,
  mediaWidth: number,
  mediaHeight: number,
  rotationDegrees: number
): PageRect {
  const r = normalizeRotation(rotationDegrees);
  const visual = visualPageDimensions(mediaWidth, mediaHeight, r);
  const { x: vx, y: vy, width: vw, height: vh } = rect;

  switch (r) {
    case 0:
      return { x: vx, y: vy, width: vw, height: vh };
    case 90:
      // /Rotate 90 = page shown 90° clockwise. Visual bottom-left maps to the
      // media RIGHT edge, so x is measured from the far side (mediaWidth =
      // visual.height). Verified against pdf.js viewport.convertToPdfPoint.
      return { x: visual.height - vy - vh, y: vx, width: vh, height: vw };
    case 180:
      return {
        x: visual.width - vx - vw,
        y: visual.height - vy - vh,
        width: vw,
        height: vh,
      };
    case 270:
      // /Rotate 270 = page shown 90° counter-clockwise. Mirror of the 90° case.
      return {
        x: vy,
        y: visual.width - vx - vw,
        width: vh,
        height: vw,
      };
    default:
      return { x: vx, y: vy, width: vw, height: vh };
  }
}

/** Inverse of visualRectToMediaBox — remap fields when page rotation changes. */
export function mediaRectToVisualBox(
  rect: PageRect,
  mediaWidth: number,
  mediaHeight: number,
  rotationDegrees: number
): PageRect {
  const r = normalizeRotation(rotationDegrees);
  const visual = visualPageDimensions(mediaWidth, mediaHeight, r);
  const { x: mx, y: my, width: mw, height: mh } = rect;

  switch (r) {
    case 0:
      return { x: mx, y: my, width: mw, height: mh };
    case 90:
      // True inverse of the corrected visualRectToMediaBox 90° case.
      return { x: my, y: visual.height - mx - mw, width: mh, height: mw };
    case 180:
      return {
        x: visual.width - mx - mw,
        y: visual.height - my - mh,
        width: mw,
        height: mh,
      };
    case 270:
      // True inverse of the corrected visualRectToMediaBox 270° case.
      return {
        x: visual.width - my - mh,
        y: mx,
        width: mh,
        height: mw,
      };
    default:
      return { x: mx, y: my, width: mw, height: mh };
  }
}

/**
 * Keep in sync with server/src/utils/pageRotation.ts.
 *
 * The local drawing frame is screen-aligned for every page rotation, so a visual
 * stroke offset passes straight through. (Previously negated dy on 90°/270° to
 * compensate for the swapped visualRectToMediaBox formulas — now corrected.)
 */
export function visualVectorToLocal(
  dx: number,
  dy: number,
  rotationDegrees: number
): { x: number; y: number } {
  const r = normalizeRotation(rotationDegrees);
  switch (r) {
    default:
      return { x: dx, y: dy };
  }
}